import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

export type ProjectsTableSyncReason =
  | 'initial'
  | 'reconnect'
  | 'mutation-error'
  | 'manual'
  | 'background';

export type ProjectsTableTrustState = 'bootstrapping' | 'live' | 'stale' | 'reconciling';

export type ProjectsTableSyncState = {
  hasHydrated: boolean;
  wsConnected: boolean;
  trustState: ProjectsTableTrustState;
  isReconciling: boolean;
  lastReconcileReason: ProjectsTableSyncReason | null;
  lastReconcileAt: number | null;
};

export const PROJECTS_TABLE_SYNC_QUERY_KEY = ['projectsTableSyncState'] as const;

const DEFAULT_SYNC_STATE: ProjectsTableSyncState = {
  hasHydrated: false,
  wsConnected: false,
  trustState: 'bootstrapping',
  isReconciling: false,
  lastReconcileReason: null,
  lastReconcileAt: null,
};

function setState(
  queryClient: QueryClient,
  updater: (prev: ProjectsTableSyncState) => ProjectsTableSyncState
): void {
  queryClient.setQueryData<ProjectsTableSyncState>(PROJECTS_TABLE_SYNC_QUERY_KEY, (prev) =>
    updater(prev ?? DEFAULT_SYNC_STATE)
  );
}

export function markProjectsTableHydrated(queryClient: QueryClient): void {
  setState(queryClient, (prev) => ({
    ...prev,
    hasHydrated: true,
    trustState: prev.isReconciling ? 'reconciling' : prev.wsConnected ? 'live' : prev.trustState,
  }));
}

export function markProjectsTableWsConnected(queryClient: QueryClient): void {
  setState(queryClient, (prev) => ({
    ...prev,
    wsConnected: true,
    trustState: prev.isReconciling ? 'reconciling' : prev.hasHydrated ? 'live' : prev.trustState,
  }));
}

export function markProjectsTableWsDisconnected(queryClient: QueryClient): void {
  setState(queryClient, (prev) => ({
    ...prev,
    wsConnected: false,
    trustState: prev.hasHydrated ? 'stale' : prev.trustState,
  }));
}

export function markProjectsTableTrustStale(queryClient: QueryClient): void {
  setState(queryClient, (prev) => ({
    ...prev,
    trustState: prev.hasHydrated ? 'stale' : prev.trustState,
  }));
}

export function markProjectsTableReconcileStarted(
  queryClient: QueryClient,
  reason: ProjectsTableSyncReason
): void {
  setState(queryClient, (prev) => ({
    ...prev,
    isReconciling: true,
    trustState: 'reconciling',
    lastReconcileReason: reason,
  }));
}

export function markProjectsTableReconcileFinished(
  queryClient: QueryClient,
  reason: ProjectsTableSyncReason
): void {
  setState(queryClient, (prev) => ({
    ...prev,
    hasHydrated: true,
    isReconciling: false,
    trustState: prev.wsConnected ? 'live' : 'stale',
    lastReconcileReason: reason,
    lastReconcileAt: Date.now(),
  }));
}

export function useProjectsTableSyncState(): ProjectsTableSyncState {
  const { data } = useQuery({
    queryKey: PROJECTS_TABLE_SYNC_QUERY_KEY,
    queryFn: () => DEFAULT_SYNC_STATE,
    staleTime: Infinity,
    initialData: DEFAULT_SYNC_STATE,
  });

  return data;
}
